<template>
  <div>
    <div class="tw:flex tw:md:flex-row tw:flex-col tw:w-full tw:justify-between tw:mb-3 tw:gap-y-3 tw:mt-6">
      <PerformanceHeader
        :aggregationType="aggregationType"
        :periodType="periodType"
        :selectedDisplayType="selectedDisplayType"
        :startDate="startDate"
        :endDate="endDate"
        @changePeriodType="changePeriodType"
        @changeAggregationType="changeAggregationType"
        @changeStartDate="changeStartDate"
        @changeEndDate="changeEndDate"
      />
      <GhostButton
        @click="changeDisplayType"
        size="big"
        class="tw:flex tw:items-center tw:gap-x-1 tw:md:ml-2 tw:hidden tw:md:flex"
      >
        <GraphIcon v-if="selectedDisplayType === 'stats'" class="stats tw:fill-gray-800 tw:size-[18px]" />
        <StatsIcon v-if="selectedDisplayType === 'graph'" class="graph tw:stroke-gray-800 tw:size-[18px]" />
        {{ selectedDisplayType === 'stats' ? $t('performance_show_graph') : $t('performance_show_table') }}
      </GhostButton>
    </div>
    <div class="tw:flex tw:flex-col tw:gap-y-3" v-if="selectedDisplayType === 'stats'">
      <div class="tw:grid tw:grid-cols-1 tw:lg:grid-cols-2 tw:gap-3 tw:my-3">
        <div v-for="item in periodSummary" :key="item.id" class="tw:basis-[calc(50%-0.5rem)]">
          <RateConnectPerformanceCard
            v-if="item.id !== 'clicks'"
            :noFilterData="hasStatistics && statsData.length === 0"
            :title="item.title"
            :hint="item.hint"
            :icon="item.icon"
            :id="item.id"
            :value="item.value"
            :progression="item.variation"
            :total="item.total"
            :isRateConnectEligible="isRateConnectEligible"
            :hasRateConnect="hasRateConnect"
          />
          <RateConnectClicksCard
            v-else
            :noFilterData="hasStatistics && statsData.length === 0"
            :title="item.title"
            :hint="item.hint"
            :icon="item.icon"
            :id="item.id"
            :value="item.value"
            :progression="item.variation"
            :total="visits"
            :partnerDistribution="partnerDistribution"
            :isRateConnectEligible="isRateConnectEligible"
            :hasRateConnect="hasRateConnect"
          />
        </div>
      </div>
    </div>

    <div v-if="selectedDisplayType === 'graph'">
      <div v-if="isFetching" class="tw:h-[400px] tw:w-full tw:animate-pulse tw:bg-gray-100 tw:rounded-xl" />
      <PerformanceGraph
        v-else-if="hasGraphData"
        containerId="performance-graph"
        :seriesData="seriesData"
        :aggregationType="aggregationType"
        :studioPlus="studioPlusTimestamp"
        :isMobile="isMobile"
      />
      <PerformanceGraphEmpty v-else />
      <div class="tw:border-t tw:border-gray-300 tw:flex tw:flex-row tw:pt-4 tw:gap-3">
        <div v-for="item in legend" :key="item.id" class="tw:flex tw:flex-row tw:items-center tw:gap-1">
          <div class="tw:w-3 tw:h-3 tw:block tw:rounded-full" :style="{ backgroundColor: item.color }" />
          <p v-html="$t(item.label)" />
        </div>
      </div>
    </div>
  </div>
</template>
<script setup>
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'

import GhostButton from '@/components/base/buttons/GhostButton.vue'
import GraphIcon from '@/components/icons/GraphIcon.svg?component'
import StatsIcon from '@/components/icons/StatsIcon.svg?component'
import PerformanceHeader from '@/components/homePage/performance/PerformanceHeader.vue'
import PerformanceGraph from '@/components/homePage/performance/PerformanceGraph.vue'
import PerformanceGraphEmpty from '@/components/homePage/performance/PerformanceGraphEmpty.vue'
import RateConnectClicksCard from './RateConnectClicksCard.vue'
import RateConnectPerformanceCard from './RateConnectPerformanceCard.vue'

import { useConversionFunnelData } from '@/components/homePage/queries/conversionFunnel'
import { selectedAccommodationId } from '@/layouts/queries'
import { useStudioPlusCampaign } from '@/components/payment/queries'
import { useIsMobile } from '@/composables/useIsMobile'

import useConversionsData from '@/components/homePage/useConversionsData.js'

import appAnalytics from '@/utils/tracking'

const { t } = useI18n()
const { isMobile } = useIsMobile()

defineProps({
  periodSummary: Array,
  hasStatistics: Boolean,
  statsData: Array,
  isRateConnectEligible: Boolean,
  hasRateConnect: Boolean,
  hasActiveCampaign: Boolean,
  visits: Number,
  partnerDistribution: Object,
})
const {
  endDate,
  startDate,
  aggregationType,
  periodType,
  changeEndDate,
  changeStartDate,
  changeAggregationType,
  changePeriodType,
} = useConversionsData()
const selectedDisplayType = ref('stats')
const changeDisplayType = () => {
  selectedDisplayType.value = selectedDisplayType.value === 'stats' ? 'graph' : 'stats'
  appAnalytics.track(appAnalytics.events.HOMEPAGE_SWITCH_GRAPH_BUTTON_CLICKED, {
    itemId: selectedAccommodationId.value,
    state: selectedDisplayType.value,
  })
}

const { isFetching, formattedGraphData } = useConversionFunnelData(startDate, endDate, aggregationType)
const { studioPlusCampaign } = useStudioPlusCampaign(selectedAccommodationId)
const studioPlusTimestamp = computed(() => {
  const dayDate = studioPlusCampaign !== null ? studioPlusCampaign.value?.createdAt.split('@')[0] : null
  return studioPlusCampaign !== null ? Math.floor(new Date(dayDate).getTime()) : null
})

const seriesData = computed(() => {
  return [
    {
      data: formattedGraphData.value?.impressions ?? [],
      color: '#007cc2 ',
      name: t('kpi_chart_views'),
      internalName: 'impressions',
      aggregationType: aggregationType.value,
    },
    {
      data: formattedGraphData.value?.clicks ?? [],
      color: '#99cc66',
      name: t('kpi_chart_visits'),
      internalName: 'clicks',
      aggregationType: aggregationType.value,
    },
    {
      data: formattedGraphData.value?.clickThroughRate ?? [],
      aggregationType: aggregationType.value,
      name: t('rc_label_ctr'),
      internalName: 'clickThroughRate',
      color: 'red',
      lineColor: 'transparent',
      marker: {
        enabled: false,
        states: {
          hover: {
            enabled: false,
          },
        },
      },
    },
  ]
})

const hasGraphData = computed(() => seriesData.value?.some(series => series.data && series.data.length > 0))
const legend = computed(() => {
  return [
    { id: 'clicks', color: '#99cc66', label: 'kpi_clicks' },
    { id: 'impressions', color: '#007cc2', label: 'kpi_views' },
  ]
})
</script>
<style scoped>
button:hover .stats {
  fill: var(--tw-color-white);
  stroke: var(--tw-color-white);
}
button:hover .graph {
  stroke: var(--tw-color-white);
}
</style>
